'use client';
import LocalizedLink from '@/components/LocalizedLink';
import {
  Field,
  Input,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { ArrowLeftIcon } from '@heroicons/react/16/solid';
import { ChevronUpDownIcon, PhoneIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { z } from 'zod';

type FormFields = {
  fields_fullName: string;
  fields_age: string;
  fields_phone: string;
  fields_event: string;
  fields_childeName: string;
};

// Схема валідації
const formSchema = z.object({
  fields_fullName: z.string().min(1, "Ім'я є обов'язковим"),
  fields_phone: z
    .string({
      required_error: "Телефон є обов'язковим",
    })
    .min(1, "Телефон є обов'язковим")
    .regex(/^\+?[0-9]{10,14}$/, 'Невірний формат номера телефону'),
  fields_age: z
    .string()
    .trim()
    .refine(
      age => {
        const num = Number(age);
        return !isNaN(num) && num >= 1 && num <= 120;
      },
      { message: 'Вік учасника має бути числом' },
    ),
  fields_event: z.string({
    required_error: 'Виберіть івент',
  }),
  fields_childeName: z.string().optional(),
});

const formSchemaChild = z.object({
  fields_fullName: z.string().min(1, "Ім'я є обов'язковим"),
  fields_phone: z
    .string({
      required_error: "Телефон є обов'язковим",
    })
    .min(1, "Телефон є обов'язковим")
    .regex(/^\+?[0-9]{10,14}$/, 'Невірний формат номера телефону'),
  fields_age: z
    .string()
    .trim()
    .refine(
      age => {
        const num = Number(age);
        return !isNaN(num) && num >= 1 && num <= 120;
      },
      { message: 'Вік учасника має бути числом' },
    ),
  fields_event: z.string({
    required_error: 'Виберіть івент',
  }),
  fields_childeName: z
    .string({
      required_error: "Вкажіть ім'я дитини",
    })
    .min(1, "Вкажіть ім'я дитиниу"),
});

export default function EventChildForm() {
  const backgroundColorClass = 'custom-gradient-green-btn';
  const router = useRouter();
  const [formData, setFormData] = useState<FormFields>({
    fields_fullName: '',
    fields_phone: '',
    fields_age: '',
    fields_event: '',
    fields_childeName: '',
  });
  const eventOptions = ['День музики, 01.10-3.10'];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<string>(eventOptions[0]);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<FormFields>({
    fields_fullName: '',
    fields_phone: '',
    fields_age: '',
    fields_event: '',
    fields_childeName: '',
  });
  const link = 'https://ap-server-8qi1.onrender.com/leads/event';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
    if (formErrors && formErrors[name as keyof FormFields]) {
      setFormErrors(prevData => ({
        ...prevData,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    setLoading(true);
    setError(null);
    setFormErrors({
      fields_fullName: '',
      fields_phone: '',
      fields_age: '',
      fields_event: '',
      fields_childeName: '',
    });
    const data = {
      fields_fullName: formData.fields_fullName,
      fields_phone: formData.fields_phone,
      fields_age: formData.fields_age,
      fields_event: event,
      fields_childeName: formData.fields_childeName,
    };

    const utmTags = JSON.parse(localStorage.getItem('utmTags') || '{}') || {};
    try {
      let validatedData;
      if (event.toLowerCase().includes('дитячий')) {
        console.log(1);
        validatedData = formSchemaChild.parse(data);
      } else {
        console.log(2);
        validatedData = formSchema.parse(data);
      }

      console.log(validatedData);
      const res = await axios.post(
        link,
        {
          name: validatedData.fields_fullName,
          phone: validatedData.fields_phone,
          age: validatedData.fields_age,
          eventType: validatedData.fields_event,
          ...utmTags,
        },
      );
      const pageLink = res.data.link;
      window.open(pageLink, '_blank');

      setSuccess(true);

      router.push('/thankyou');
    } catch (error) {
      console.log(error);
      if (error instanceof z.ZodError) {
        const newErrors: FormFields = {
          fields_fullName: '',
          fields_phone: '',
          fields_age: '',
          fields_event: '',
          fields_childeName: '',
        };

        error.issues.forEach(err => {
          const fieldName = err.path[0] as keyof FormFields;
          newErrors[fieldName] = err.message;
        });
        setFormErrors(newErrors);
      } else {
        setError('Форма не відправлена. Спробуйте, будь ласка, ще раз.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="relative z-10 lg:fixed">
        <nav
          aria-label="Global"
          className="relative mx-auto flex items-center lg:gap-4 justify-between p-6 lg:px-2 xl:px-8 z-50"
        >
          <div className="flex">
            <LocalizedLink href={'/'} className="-m-1.5 p-1.5">
              <span className="sr-only">App Education</span>
              <Image
                width={100}
                height={38}
                alt=""
                src={'/assets/icon/LogoRevers.svg'}
                className="h-8 w-32 hidden lg:block"
              />
              <Image
                width={100}
                height={38}
                alt=""
                src={'/assets/icon/AP.svg'}
                className="h-8 w-16 lg:hidden"
              />
            </LocalizedLink>
          </div>
          <div className="flex justify-end items-center gap-x-8">
            <a
              href="tel:+380936707129"
              className="flex gap-x-2 items-center text-xs xl:text-sm font-semibold leading-6 text-white"
            >
              <PhoneIcon className="h-[20px] w-[20px] text-white" />
              +38 (093) 670 71 29
            </a>
          </div>
        </nav>
      </header>
      <div className="w-full min-h-screen flex flex-col items-center justify-center">
        <div className="h-full w-full overflow-hidden">
          <div className="blur-gradient-green -bottom-32 -right-[60%] -z-50"></div>
          <div className="blur-gradient-green -left-[55%] -top-32 -z-50"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 max-w-9xl mx-auto rounded-3xl mt-8">
          <div className="relative flex flex-col justify-center items-center px-6 pb-8 max-lg:mt-8 lg:px-8 lg:col-span-6">
            <div className="flex flex-col justify-between mx-auto lg:pl-8 lg:max-w-2xl lg:mx-0 max-w-lg">
              <LocalizedLink
                href={'/'}
                className="text-white mb-6 flex justify-start items-baseline"
              >
                <div className="flex justify-center items-center">
                  <div className="border border-white rounded-full p-2 mr-4">
                    <ArrowLeftIcon className="h-full w-4 md:w-5 " />
                  </div>
                  <span className="text-sm lg:text-xl">Повернутися на головну сторінку</span>
                </div>
              </LocalizedLink>
              <p
                className="max-lg:text-center text-3xl flex-1 lg:text-5xl font-bold tracking-tight text-white"
                style={{ lineHeight: '1.2' }}
              >
                Залишіть ваші дані, необхідні для участі в івенті
              </p>
              <div className="hidden lg:flex flex-col mt-12">
                <p className="text-white text-lg mb-4">А також підписуйтесь на нас у соцмережах:</p>
                <div className="flex gap-3">
                  <div
                    className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                  >
                    <Link href={'https://www.facebook.com/ap.edu.centre/'} target="_blank">
                      <Image
                        width={64}
                        height={64}
                        className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                        src="/assets/icon/FBSolid.svg"
                        alt=""
                      />
                    </Link>
                  </div>
                  <div
                    className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                  >
                    <Link
                      href={'https://www.tiktok.com/@ap.education.center?_t=8qLAl2a7AUH&_r=1'}
                      target="_blank"
                    >
                      <Image
                        width={64}
                        height={64}
                        className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                        src="/assets/icon/TKSolid.svg"
                        alt=""
                      />
                    </Link>
                  </div>
                  <div
                    className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                  >
                    <Link href={'https://www.instagram.com/ap.education'} target="_blank">
                      <Image
                        width={64}
                        height={64}
                        className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                        src="/assets/icon/InstSolid.svg"
                        alt=""
                      />
                    </Link>
                  </div>
                  <div
                    className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                  >
                    <Link
                      href={'https://www.linkedin.com/company/ap-education-center'}
                      target="_blank"
                    >
                      <Image
                        width={64}
                        height={64}
                        className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                        src="/assets/icon/LinkSolid.svg"
                        alt=""
                      />
                    </Link>
                  </div>
                  <div
                    className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                  >
                    <Link href={'https://www.youtube.com/@ap.education.center'} target="_blank">
                      <Image
                        width={64}
                        height={64}
                        className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                        src="/assets/icon/YTSolid.svg"
                        alt=""
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form
            autoComplete="off"
            onSubmit={handleSubmit}
            className="px-4 lg:col-start-7 lg:col-span-6"
          >
            <div className="mx-auto lg:max-w-2xl max-w-lg lg:mr-0 rounded-3xl bg-white relative">
              {loading ? (
                <div className="absolute inset-0 flex justify-center items-center text-center w-full h-full bg-white z-50 rounded-3xl">
                  <div className="text-xl">Зачекайте реєструємо вас на подію...</div>
                </div>
              ) : null}
              <div className="px-4 py-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Listbox value={event} onChange={setEvent}>
                      <Label className="ml-2 text-[0.7rem] text-gray-600">Оберіть подію*</Label>
                      <div className="relative mx-auto">
                        <ListboxButton className="relative z-20 w-full cursor-default rounded-full bg-white py-2 px-4 text-left text-gray-600 shadow-sm ring-1 ring-inset ring-green-300 focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6">
                          <div className="flex justify-between">
                            <span className="block truncate text-gray-600">{event}</span>
                          </div>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                              aria-hidden="true"
                              className="h-5 w-5 text-gray-400"
                            />
                          </span>
                        </ListboxButton>

                        <ListboxOptions
                          transition
                          className="absolute z-10 -mt-5 pt-6 max-h-60 w-full overflow-auto rounded-md bg-[#F1F1F1] py-1 text-base shadow-lg focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                        >
                          {eventOptions.map((option, index) => (
                            <ListboxOption
                              key={`event-${index}`}
                              value={option}
                              className="group flex justify-between relative cursor-default select-none py-2 pl-8 pr-9 text-gray-500 data-[focus]:bg-green-600 data-[focus]:text-white"
                            >
                              <span className="block truncate">{option}</span>
                            </ListboxOption>
                          ))}
                        </ListboxOptions>
                      </div>
                    </Listbox>
                    {formErrors.fields_event && (
                      <p className="text-red-500 text-[0.7rem] ml-2">{formErrors.fields_event}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <Field>
                      <Label className="ml-2 text-[0.7rem] text-gray-600">
                        Ваше прізвище та ім’я*
                      </Label>
                      <Input
                        id="fields_fullName"
                        autoComplete="off"
                        name="fields_fullName"
                        type="text"
                        value={formData.fields_fullName}
                        onChange={handleInputChange}
                        className={clsx(
                          'block w-full rounded-full border-none ring-1 ring-inset ring-green-300 py-2 px-4',
                          'focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6',
                          formErrors && formErrors?.fields_fullName ? 'ring-red-400' : '',
                        )}
                      />
                      {formErrors.fields_fullName && (
                        <p className="text-red-500 text-[0.7rem] ml-2">
                          {formErrors.fields_fullName}
                        </p>
                      )}
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field>
                      <Label className="ml-2 text-[0.7rem] text-gray-600">Телефон*</Label>
                      <Input
                        id="fields_phone"
                        autoComplete="off"
                        name="fields_phone"
                        type="text"
                        value={formData.fields_phone}
                        onChange={handleInputChange}
                        className={clsx(
                          'block w-full rounded-full border-none ring-1 ring-inset ring-green-300 py-2 px-4',
                          'focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6',
                          formErrors && formErrors?.fields_phone ? 'ring-red-400' : '',
                        )}
                      />
                      {formErrors.fields_phone && (
                        <p className="text-red-500 text-[0.7rem] ml-2">{formErrors.fields_phone}</p>
                      )}
                    </Field>
                  </div>
                  {event.toLowerCase().includes('дитячий') && (
                    <div className="sm:col-span-2">
                      <Field>
                        <Label className="ml-2 text-[0.7rem] text-gray-600">Ім’я дитини</Label>
                        <Input
                          id="fields_childeName"
                          name="fields_childeName"
                          type="text"
                          value={formData.fields_childeName}
                          onChange={handleInputChange}
                          className={clsx(
                            'block w-full rounded-full border-none ring-1 ring-inset ring-green-300 py-2 px-4',
                            'focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6',
                            formErrors && formErrors?.fields_childeName ? 'ring-red-400' : '',
                          )}
                        />
                        {formErrors.fields_childeName && (
                          <p className="text-red-500 text-[0.7rem] ml-2">
                            {formErrors.fields_childeName}
                          </p>
                        )}
                      </Field>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Field>
                      <Label className="ml-2 text-[0.7rem] text-gray-600">Вік учасника*</Label>
                      <Input
                        id="fields_age"
                        name="fields_age"
                        type="text"
                        value={formData.fields_age}
                        onChange={handleInputChange}
                        className={clsx(
                          'block w-full rounded-full border-none ring-1 ring-inset ring-green-300 py-2 px-4',
                          'focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6',
                          formErrors && formErrors?.fields_age ? 'ring-red-400' : '',
                        )}
                      />
                      {formErrors.fields_age && (
                        <p className="text-red-500 text-[0.7rem] ml-2">{formErrors.fields_age}</p>
                      )}
                    </Field>
                  </div>
                </div>
                <div className="mt-8 flex max-sm:flex-col justify-start">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`${backgroundColorClass} uppercase rounded-full px-8 py-2.5 text-center text-md font-fixel font-bold text-white shadow-sm focus-visible:outline`}
                  >
                    {loading ? 'Надсилання...' : 'Надіслати'}
                  </button>
                  {error && <p className="mt-2 text-red-500 text-sm">{error}</p>}
                  {success && (
                    <p className="mt-2 ml-2 text-green-500 text-sm">Форма успішно надіслана!</p>
                  )}
                </div>
              </div>
            </div>
          </form>
          <div className="flex flex-col lg:hidden gap-3 mt-8 mx-auto mb-8">
            <p className="text-white text-center text-lg mb-4">
              А також підписуйтесь на нас у соцмережах:
            </p>
            <div className="flex  gap-3 my-4 mx-auto">
              <div
                className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
              >
                <Link href={'https://www.facebook.com/ap.edu.centre/'} target="_blank">
                  <Image
                    width={64}
                    height={64}
                    className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                    src="/assets/icon/FBSolid.svg"
                    alt=""
                  />
                </Link>
              </div>
              <div
                className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
              >
                <Link
                  href={'https://www.tiktok.com/@ap.education.center?_t=8qLAl2a7AUH&_r=1'}
                  target="_blank"
                >
                  <Image
                    width={64}
                    height={64}
                    className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                    src="/assets/icon/TKSolid.svg"
                    alt=""
                  />
                </Link>
              </div>
              <div
                className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
              >
                <Link href={'https://www.instagram.com/ap.education'} target="_blank">
                  <Image
                    width={64}
                    height={64}
                    className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                    src="/assets/icon/InstSolid.svg"
                    alt=""
                  />
                </Link>
              </div>
              <div
                className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
              >
                <Link href={'https://www.linkedin.com/company/ap-education-center'} target="_blank">
                  <Image
                    width={64}
                    height={64}
                    className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                    src="/assets/icon/LinkSolid.svg"
                    alt=""
                  />
                </Link>
              </div>
              <div
                className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
              >
                <Link href={'https://www.youtube.com/@ap.education.center'} target="_blank">
                  <Image
                    width={64}
                    height={64}
                    className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                    src="/assets/icon/YTSolid.svg"
                    alt=""
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
